import { Component, Suspense, lazy, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/i18n";

const RobotScene = lazy(() => import("@/components/RobotScene"));

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
    return (
        <span className={`block overflow-hidden ${className}`}>
            <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

function StaticVisual() {
    return (
        <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
            <div className="absolute h-52 w-52 rounded-full bg-brand/10 blur-[90px]" />
            <motion.img
                src="/robot-poster.png"
                alt="Robot mascotte RED PRIVATE SRL"
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="relative h-[58vh] max-h-[560px] w-auto object-contain drop-shadow-[0_0_50px_rgba(244,28,51,0.25)]"
            />
        </div>
    );
}

class SceneBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }
    static getDerivedStateFromError() {
        return { failed: true };
    }
    render() {
        return this.state.failed ? <StaticVisual /> : this.props.children;
    }
}

export default function Hero() {
    const { ui } = useLang();
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
    );

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)");
        const onChange = (e) => setIsMobile(e.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    return (
        <section id="hero" data-testid="hero-section" className="grain relative min-h-screen overflow-hidden bg-ink">
            {/* Cinematic backdrop */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_28%_at_50%_30%,rgba(244,28,51,0.07),transparent_62%)] md:hidden" />
            <div className="absolute inset-0 hidden bg-[radial-gradient(ellipse_60%_50%_at_65%_40%,rgba(244,28,51,0.12),transparent_65%)] md:block" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_110%,rgba(20,20,28,0.9),transparent_70%)]" />
            <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/60 to-transparent" />

            {/* 3D robot / static fallback */}
            <div className="absolute inset-0 md:left-[12%]" data-testid="hero-robot-wrapper">
                {isMobile ? (
                    <StaticVisual />
                ) : (
                    <SceneBoundary>
                        <Suspense fallback={<StaticVisual />}>
                            <RobotScene />
                        </Suspense>
                    </SceneBoundary>
                )}
            </div>

            {/* Mobile readability gradient */}
            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink via-ink/70 to-transparent md:hidden" />

            {/* Copy */}
            <div className="pointer-events-none relative z-10 mx-auto flex min-h-screen w-full max-w-7xl flex-col justify-end px-5 pb-28 pt-32 sm:px-8 md:justify-center md:pb-0">
                <div className="max-w-2xl">
                    <motion.p
                        data-testid="hero-eyebrow"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.2 }}
                        className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-brand sm:text-xs"
                    >
                        {ui.hero.eyebrow}
                    </motion.p>

                    <h1 className="font-display text-[13.5vw] font-extrabold uppercase leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-[5.2rem]">
                        <MaskedLine delay={0.35}>{ui.hero.l1}</MaskedLine>
                        <MaskedLine delay={0.48}>
                            {ui.hero.l2a} <span className="text-brand">{ui.hero.l2b}</span>
                        </MaskedLine>
                        <MaskedLine delay={0.61} className="text-outline">
                            {ui.hero.l3}
                        </MaskedLine>
                    </h1>

                    <motion.p
                        data-testid="hero-tagline"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.9, ease: EASE }}
                        className="mt-7 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base"
                    >
                        {ui.hero.tagline}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1.05, ease: EASE }}
                        className="pointer-events-auto mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-cta-button"
                            onClick={() => scrollToId("#produzioni")}
                            className="group flex items-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-white shadow-[0_0_36px_rgba(244,28,51,0.35)] transition-all duration-300 hover:bg-brand-dark hover:shadow-[0_0_48px_rgba(244,28,51,0.5)]"
                        >
                            {ui.hero.cta1}
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <button
                            data-testid="hero-secondary-cta"
                            onClick={() => scrollToId("#contatti")}
                            className="group flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:border-brand hover:text-brand"
                        >
                            {ui.hero.cta2}
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                    </motion.div>
                </div>
            </div>

            {/* Side label */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 1 }}
                className="absolute right-8 top-1/2 hidden -translate-y-1/2 rotate-90 lg:block"
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.5em] text-slate-500">
                    {ui.hero.side}
                </span>
            </motion.div>

            {/* Scroll indicator */}
            <motion.button
                data-testid="hero-scroll-indicator"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                onClick={() => scrollToId("#produzioni")}
                className="absolute bottom-7 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-slate-500 transition-colors hover:text-brand md:flex"
                aria-label={ui.hero.scroll}
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.3em]">{ui.hero.scroll}</span>
                <ChevronDown className="h-4 w-4 animate-bounce" />
            </motion.button>
        </section>
    );
}
